import { cn } from '@/lib/utils';
import { EllipsisVertical } from 'lucide-react';
import React, { useState } from 'react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import {
    MenuItemProps,
    MenuRenderer,
    MenuSeparatorProps,
    SubmenuProps,
    ThreadMenuItemsProps,
    useThreadMenuItems
} from './ThreadMenuItems';

interface Props extends ThreadMenuItemsProps {
    className?: string;
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function ThreadOptions({
    onDelete,
    onRename,
    onAddToProject,
    onCreateNewProject,
    onRemoveFromProject,
    onPinToOrg,
    onShareThread,
    folders = [],
    className,
    isOpen: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    isInProject = false
}: Props) {
    const [internalOpen, setInternalOpen] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const onOpenChange =
        controlledOnOpenChange !== undefined
            ? controlledOnOpenChange
            : setInternalOpen;

    const { renderMenuItems } = useThreadMenuItems({
        onDelete,
        onRename,
        onAddToProject,
        onCreateNewProject,
        onRemoveFromProject,
        onPinToOrg,
        onShareThread,
        folders,
        isInProject
    });

    // ShadCN dropdown renderer implementation
    const dropdownRenderer: MenuRenderer = {
        MenuItem: ({
            onClick,
            icon,
            children,
            className: itemClassName,
            destructive,
            style
        }: MenuItemProps) => (
            <DropdownMenuItem
                onClick={onClick}
                variant={destructive ? 'destructive' : 'default'}
                className={cn('rounded-lg px-2.5', itemClassName)}
                style={style}
            >
                {icon}
                {children}
            </DropdownMenuItem>
        ),
        MenuSeparator: ({
            className: separatorClassName
        }: MenuSeparatorProps) => (
            <DropdownMenuSeparator className={separatorClassName} />
        ),
        Submenu: ({ trigger, children }: SubmenuProps) => (
            <DropdownMenuSub>
                <DropdownMenuSubTrigger className="px-2.5 rounded-lg">
                    {trigger}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                    {children}
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        )
    };

    const menuItems = renderMenuItems(dropdownRenderer);

    return (
        <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation();
                    }}
                    onTouchMove={(e) => {
                        e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                        e.stopPropagation();
                    }}
                    id="thread-options"
                    className={cn(
                        'inline-flex items-center justify-center self-center size-7 rounded-md cursor-pointer [&_svg]:size-4',
                        'text-muted-foreground hover:text-foreground hover:bg-foreground/10 data-[state=open]:bg-foreground/10 data-[state=open]:text-foreground transition-colors focus-visible:outline-hidden',
                        className
                    )}
                >
                    <EllipsisVertical />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                forceMount
                className="p-1.5 rounded-xl"
            >
                {menuItems}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
